import convert from 'color-convert';
import { ActorID } from './server/utils/apify/ActorID';
import { Feature, Permission, type ClaimAndTrademarkApproval } from '@prisma/client';

export enum StatusOptions {
  FIXED = 'FIXED',
  SKIPPED = 'SKIPPED',
  APPROVED = 'APPROVED',
  UNVERIFIED = 'UNVERIFIED',
}

export const approvalMap: Record<ClaimAndTrademarkApproval, string> = {
  IN_USE_CONSIDER_REVIEW: 'In Use',
  UNDER_REVIEW: 'Under Review',
  LIMITED_APPROVAL: 'Limited Approval',
  STANDING_APPROVAL: 'Standing Approval',
  ARCHIVED: 'Archived',
};

export const frameioHosts = ['f.io', 'frame.io', 'app.frame.io', 'assets.frame.io'];

/** Public Review docs (GitBook). Root docs.tryprotege.com is Hookshot — keep Review under /review. */
export const REVIEW_DOCS_BASE_URL = 'https://docs.tryprotege.com/review';

/** Guard token limit in AI prompts. This needs to be aligned with the backend API */
export const PAGE_WORDS_COUNT_LIMIT = 3500; // ~5120 token

export const UNKNOWN_BRAND_ID = 'unassigned';

export enum AvailableFeedbackLabelColors {
  // blue = 'blue', // colors are really hard to see
  // darkblue = 'darkblue',
  // darkred = 'darkred',
  gray = 'gray',
  lightgray = 'lightgray',
  limegreen = 'limegreen',
  olive = 'olive',
  pink = 'pink',
  purple = 'purple',
  orange = 'orange',
  red = 'red',
  teal = 'teal',
  turquoise = 'turquoise',
  yellow = 'yellow',
}

/** Distinct hues — one slot per family, no near-duplicates. */
export const FEEDBACK_LABEL_TOKEN_COLOR: Record<AvailableFeedbackLabelColors, string> = {
  [AvailableFeedbackLabelColors.gray]: '#52525b',
  [AvailableFeedbackLabelColors.lightgray]: '#d4d4d8',
  [AvailableFeedbackLabelColors.limegreen]: '#22c55e',
  [AvailableFeedbackLabelColors.olive]: '#c4a035',
  [AvailableFeedbackLabelColors.pink]: '#ec4899',
  [AvailableFeedbackLabelColors.purple]: '#8b5cf6',
  [AvailableFeedbackLabelColors.orange]: '#f97316',
  [AvailableFeedbackLabelColors.red]: '#ef4444',
  [AvailableFeedbackLabelColors.teal]: '#14b8a6',
  [AvailableFeedbackLabelColors.turquoise]: '#38bdf8',
  [AvailableFeedbackLabelColors.yellow]: '#eab308',
};

const FEEDBACK_LABEL_COLOR_ALIASES: Record<AvailableFeedbackLabelColors, string[]> = {
  [AvailableFeedbackLabelColors.gray]: ['#71717a'],
  [AvailableFeedbackLabelColors.lightgray]: ['#a1a1aa'],
  [AvailableFeedbackLabelColors.limegreen]: ['#69d100', '#32cd32'],
  [AvailableFeedbackLabelColors.olive]: ['#3f6212', '#84cc16'],
  [AvailableFeedbackLabelColors.pink]: ['#db2777', '#f43f5e'],
  [AvailableFeedbackLabelColors.purple]: ['#6b21a8'],
  [AvailableFeedbackLabelColors.orange]: ['#f59e0b'],
  [AvailableFeedbackLabelColors.red]: [],
  [AvailableFeedbackLabelColors.teal]: ['#0f766e'],
  [AvailableFeedbackLabelColors.turquoise]: ['#39a0df'],
  [AvailableFeedbackLabelColors.yellow]: ['#ca8a04'],
};

export const defaultFeedbackColor = FEEDBACK_LABEL_TOKEN_COLOR[AvailableFeedbackLabelColors.limegreen];

/** Map a stored label hex (token, prior token, or CSS keyword) onto the current swatch. */
export function resolveFeedbackLabelColor(stored?: string | null) {
  if (!stored) return defaultFeedbackColor;
  const lower = stored.toLowerCase();
  for (const keyword of Object.values(AvailableFeedbackLabelColors)) {
    const token = FEEDBACK_LABEL_TOKEN_COLOR[keyword];
    const legacy = `#${convert.keyword.hex(keyword)}`.toLowerCase();
    const aliases = FEEDBACK_LABEL_COLOR_ALIASES[keyword];
    if (lower === token || lower === legacy || aliases.includes(lower)) return token;
  }
  return lower;
}

export const SpecialPolicyFeedbackLabel = {
  STANDARD_CLAIM: {
    name: 'Product Claim',
    color: FEEDBACK_LABEL_TOKEN_COLOR[AvailableFeedbackLabelColors.limegreen],
  },
  STANDARD_TRADEMARK: {
    name: 'Brand Usage',
    color: FEEDBACK_LABEL_TOKEN_COLOR[AvailableFeedbackLabelColors.red],
  },
};

/** App listing options for Apify scanner */
export enum AppListingType {
  GOOGLE_PLAY = 'GOOGLE_PLAY',
  APP_STORE = 'APP_STORE',
}

export const getAppListingTypeFromActorId = (actorId?: string) => {
  if (!actorId) return undefined;
  if ((actorId as ActorID) === ActorID.GOOGLE_PLAY_STORE) return AppListingType.GOOGLE_PLAY;
  if ((actorId as ActorID) === ActorID.APPLE_APP_STORE) return AppListingType.APP_STORE;
  return undefined;
};

export enum LocalStorageKeys {
  TEST_KEY_PREFIX = 'policy_test_',
  ROUTE_HISTORY = 'route_history',
  LOGS_SEARCH_PARAMS = 'logs_search_params',
  INFLUENCER_MONITORING_SEARCH_PARAMS = 'influencer_monitoring_filters_search_params',
  AGENT_THREADS = 'agent_threads',
}

export const confidenceThreshold = {
  high: 0.85,
  medium: 0.75,
};

export enum SyncScope {
  BRANDS = 'BRANDS',
  EVAL_TYPES = 'EVAL_TYPES',
  TRADEMARKS = 'TRADEMARKS',
}

export enum Routes {
  PROJECTS = '/projects',
  DECISION_STORE = '/decision-store',
  SETTINGS = '/settings',
  POLICY_ENGINE = '/policy-engine',
  CONTENT_SCANNER = '/content-scanner',
  CLAIMS_APPROVAL = '/claims-approval',
  REMEDIATION = '/remediation',
  INFLUENCER_MONITORING = '/influencer-monitoring',
  DOCUMENTS = '/documents',
  ARCHIVE_QUEUE = '/archive-queue',
  COMMUNICATION_REVIEW = '/communication-review',
  EMPLOYEE_REVIEW = '/employee-review',
  SELF_REPORTING = '/self-reporting',
  NOTIFICATIONS = '/notifications',
  ASSISTANT = '/assistant',
}

export const BASE_TIMEZONE = 'America/New_York';

export const SHUTDOWN_TEMPORAL_WORKERS = 'shutdown-temporal-workers';

export const BRAND_DEFAULTS_VARIABLE = 'defaultBrandAttributes';

export const featurePermissionMap: Record<Feature, Permission[]> = {
  [Feature.ARCHIVE_QUEUE]: [Permission.ARCHIVE_QUEUE],
  [Feature.BRAND]: [],
  [Feature.DOCUMENTS]: [],
  [Feature.MARKETING_TASKS]: [Permission.VIEW_MARKETING_TASK, Permission.EDIT_MARKETING_TASK],
  [Feature.DECISION_STORE]: [],
  [Feature.CLAIMS_APPROVAL]: [],
  [Feature.POLICY_ENGINE]: [Permission.VIEW_POLICY_ENGINE, Permission.EDIT_POLICY_ENGINE],
  [Feature.CONTENT_SCANNER]: [Permission.CONTENT_SCANNER, Permission.EDIT_SCANNER_JOB],
  [Feature.INFLUENCER_MONITORING]: [Permission.INFLUENCER_MONITORING],
  [Feature.REMEDIATION]: [Permission.REMEDIATION_QUEUE],
  [Feature.EXT_FIGMA]: [Permission.FIGMA],
  [Feature.EXT_GOOGLE]: [Permission.GOOGLE_DOC],
  [Feature.EXT_OFFICE]: [Permission.WORD],
  [Feature.EXT_ADOBE]: [Permission.PSD],
  [Feature.FILE_GOOGLE]: [Permission.EDIT_FILE_ACCESS],
  [Feature.FILE_SHAREPOINT]: [Permission.EDIT_FILE_ACCESS],
  [Feature.FILE_FRAME]: [Permission.EDIT_FILE_ACCESS],
  [Feature.FILE_AIRTABLE]: [Permission.EDIT_FILE_ACCESS],
  [Feature.COMMUNICATION_REVIEW]: [
    Permission.VIEW_COMMUNICATION_REVIEW,
    Permission.EDIT_COMMUNICATION_REVIEW_FILTER,
    Permission.EDIT_COMMUNICATION_REVIEW_INTEGRATION,
  ],
  [Feature.EMPLOYEE_REVIEW]: [Permission.VIEW_EMPLOYEE_REVIEW],
  [Feature.ASSISTANT]: [Permission.ASSISTANT],
  [Feature.SHAREABLE_LINKS]: [],
  [Feature.REG_DATA_CFPB]: [],
  [Feature.REG_DATA_FINRA]: [],
  [Feature.REG_DATA_NAD_PUBLIC]: [],
  [Feature.REG_DATA_SEC]: [],
  [Feature.ENTERPRISE]: [],
};

export const INPUT_VARIABLE_REFERENCE_PREFIX = 'inputVariable.';

export const AGENT_NAME = 'agent';
